<script>
import {
  authComputed,
  mediaComputed,
  mediaMethods,
  modalMethods,
} from '@state/helpers';
import { counterMixin } from '@mixins/counter-mixin';
import resize from '@utils/image-sizer';

export default {
  components: {},
  props: {
    existingPhoto: {
      type: String,
      required: false,
      default: null,
    },
    helperText: {
      type: String,
      required: false,
      default: null,
    },
    hidePlaceholder: {
      type: Boolean,
      required: false,
      default: false,
    },
    hidePhotoPreview: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    type: {
      type: String,
      required: false,
      default: 'cover',
    },
    requestType: {
      type: String,
      required: false,
      default: 'orgs',
    },
    hideButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCloudinaryPhoto: {
      type: Boolean,
      required: false,
    },
    isExpandable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      photoPreview: null,
      photoError: null,
      timeRemaining: 0,
      timeStarted: null,
      timeInterval: null,
    };
  },
  mixins: [counterMixin],
  computed: {
    ...authComputed,
    ...mediaComputed,
    existingPhotoUrl() {
      return resize(this.existingPhoto, 325, 185);
    },
    showExistingPhoto() {
      return !this.photoPreview && this.existingPhoto;
    },
    showPlacholder() {
      return !this.photoPreview && !this.existingPhoto && !this.hidePlaceholder;
    },
    currentUploadProgress() {
      const scopedUpload = this.signedPostData.find((upload) => {
        return (
          upload.type === this.type && upload.requestType === this.requestType
        );
      });

      return scopedUpload ? scopedUpload.uploadProgress : 0;
    },
    currentUploadSuccess() {
      const scopedUpload = this.signedPostData.find((upload) => {
        return (
          upload.type === this.type && upload.requestType === this.requestType
        );
      });

      return scopedUpload ? scopedUpload.success : false;
    },
    currentUploadIndex() {
      const scopedUpload = this.signedPostData.findIndex((upload) => {
        return (
          upload.type === this.type && upload.requestType === this.requestType
        );
      });

      return scopedUpload;
    },
    currentUploadData() {
      return this.signedPostData.find((upload) => {
        return upload.type === 'video';
      });
    },
    duration() {
      const seconds = this.timeRemaining;
      const unit = this.$_getTimeUnit(seconds);
      let result =
        seconds > 0 ? `${(seconds / 60).toFixed(2)} ` + unit : '0' + ' seconds';
      if (seconds < 60 && seconds > 0) {
        result = `${seconds.toFixed(0)} ` + unit;
      }
      return result;
    },
    styling() {
      return {
        width: this.currentUploadProgress + '%',
        'background-color': 'rgba($color-primary-one-wo-rgb, 0.7)',
      };
    },
  },
  mounted() {},
  methods: {
    ...mediaMethods,
    ...modalMethods,
    cancelUploadLocal() {
      this.cancelUpload({ index: this.currentUploadIndex });
    },
    async handleS3Photo(image) {
      try {
        await this.getS3UploadUrl({
          file: image,
          orgId: this.userOrgId,
          requestType: this.requestType || 'orgs',
          trainerId: this.trainerId || '0',
          type: this.type,
        });
      } catch (e) {
        console.log(e);
      }
    },
    async handleCloudinaryPhoto(image) {
      this.getCloudinaryUploadUrl();
      this.setRecipeImageBlob(image);
    },
    async onFileChange(evt) {
      this.timeStarted = new Date();
      this.timeInterval = setInterval(() => {
        if (
          this.currentUploadData !== undefined &&
          this.currentUploadData.file &&
          this.currentUploadData.file.size &&
          this.timeStarted
        ) {
          const totalBytes = this.currentUploadData.file.size;
          const uploadedBytes =
            this.currentUploadData.file.size *
            (this.currentUploadData.uploadProgress / 100);
          const timeElapsed = new Date() - this.timeStarted;
          const uploadSpeed = uploadedBytes / (timeElapsed / 1000);
          const timeRemaining = (totalBytes - uploadedBytes) / uploadSpeed;
          this.timeRemaining = timeRemaining;
        }
      }, 1000);

      await this.setIsNewMediaAsset(true);
      const image = evt.target.files[0];
      const reader = new FileReader();
      if (image.size >= 10000000) {
        this.photoError =
          'Image is too large. Please keep images to less than 10 mbs.';
      } else {
        this.photoError = null;
        reader.readAsDataURL(image);
        reader.onload = (evt) => {
          this.photoPreview = evt.target.result;
          this.setTempPhotoPreview(evt.target.result);
        };

        if (this.isCloudinaryPhoto) {
          await this.handleCloudinaryPhoto(image);
        } else {
          await this.handleS3Photo(image);
          await this.uploadPhoto({
            model: this.requestType,
            option: this.type,
          });
        }
      }
    },
    onViewPhoto(src) {
      if (this.isExpandable) {
        this.setSelectedImage({ src });
        this.$bvModal.show('fullsize-image-modal');
      }
    },
  },
  watch: {
    currentUploadProgress(val) {
      if (val === 100) {
        setTimeout(() => {
          clearInterval(this.timeInterval);
        }, 3000);
      }
    },
  },
};
</script>

<template>
  <div class="photo-uploader">
    <div class="photo-preview">
      <img
        v-if="showExistingPhoto"
        :src="existingPhotoUrl"
        :style="{ cursor: isExpandable ? 'pointer' : 'unset' }"
        @click="onViewPhoto(existingPhoto)"
      />
      <img
        v-if="photoPreview && !hidePhotoPreview"
        :src="photoPreview"
        @click="onViewPhoto(photoPreview)"
      />
      <img
        v-if="showPlacholder"
        src="@assets/images/placeholder-photo-upload.svg"
      />
      <span v-if="currentUploadProgress > 0" class="progress">
        <div :style="styling"
          ><span class="progress-title">{{
            currentUploadProgress === 100 ? 'Uploaded' : 'Uploading'
          }}</span
          ><span class="progress-number"
            >{{ currentUploadProgress }}%</span
          ></div
        >
      </span>
    </div>
    <label v-if="title" for="file">{{ title }}</label>
    <p v-if="helperText" class="helper-text margin-bottom">
      {{ helperText }}
    </p>
    <p v-if="currentUploadProgress > 0" class="time-remaining"
      >About {{ duration }} remaining</p
    >
    <p class="helper-text text-danger" v-show="photoError">{{ photoError }}</p>
    <input
      v-if="
        !hideButton && !(currentUploadProgress > 0 && !currentUploadSuccess)
      "
      type="file"
      class="input-photo btn-secondary"
      name="file"
      accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
      @change="onFileChange"
    />
    <BaseButton
      v-if="currentUploadProgress > 0 && !currentUploadSuccess"
      type="button"
      class="btn btn-secondary btn-danger"
      @click.prevent="cancelUploadLocal"
    >
      Cancel Upload
    </BaseButton>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.photo-preview {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  position: relative;
  width: 50%;

  img {
    width: 100%;
  }
  .progress {
    align-items: center;
    background-color: rgba($color-white-wo-rgb, 0.5);
    bottom: 0;
    display: flex;
    height: 25%;
    position: absolute;
    width: 100%;
    div {
      align-items: center;
      display: flex;
      height: 100%;
      justify-content: space-around;
      .progress-title {
        color: white;
        font-style: normal;
        font-weight: bold;
        left: 1.5rem;
        position: absolute;
        @include calc-font-size(1.1);
      }
      .progress-number {
        color: white;
        font-style: normal;
        font-weight: bold;
        position: absolute;
        right: 1.5rem;
        @include calc-font-size(1.1);
      }
    }
  }
}
.upload-actions {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  .text {
    width: 60%;
  }
  @media (max-width: 1050px) {
    align-items: unset;
    flex-direction: column;
    .text {
      width: 100%;
    }
  }
}

.btn-danger {
  height: 3.9rem;
  width: 16rem;
}

.input-photo {
  background-color: transparent;
  @extend %input-file-upload;

  &::before {
    @extend %input-file-upload-btn;

    color: $color-action;
    content: 'Upload New Image';
  }

  &:hover {
    &::before {
      color: $color-off-white;
    }
  }
}

.time-remaining {
  opacity: 0.5;
}
</style>
